// Carteles (§1.5): el tilde de cada plantilla, las hojas del mes, la vista previa y un
// solo botón que baja los dos PDF. Plantillas, letras y ajustes: PlantillasPage.
import { Fragment, useEffect, useMemo, useState } from "react";
import { DateTime } from "luxon";
import { Check, ChevronLeft, ChevronRight, Download, Minus } from "lucide-react";
import type { Pharmacy, Shift } from "../types";
import { Alert, Button, Card, Empty, Field, ICON, Seg, Select, Spinner, cx, useToast } from "./ui";
import { armarHojas, diasDeHoja, etiquetaDia, farmaciasDelDia, generarCarteles, nombreEnCartel } from "./carteles/generar";
import { cargarPreset } from "./carteles/preset";
import { bajarPlantilla, leerMetaPlantilla, motivoDeFalla, type Orientacion, type PlantillaMeta } from "./carteles/plantillasApi";

const MESES = ["enero", "febrero", "marzo", "abril", "mayo", "junio", "julio", "agosto", "septiembre", "octubre", "noviembre", "diciembre"];
const DOW3 = ["lun", "mar", "mié", "jue", "vie", "sáb", "dom"]; // weekday de luxon: 1 = lunes
const MON3 = ["ene", "feb", "mar", "abr", "may", "jun", "jul", "ago", "sep", "oct", "nov", "dic"];
const ORIENTACIONES: { value: Orientacion; label: string }[] = [
  { value: "horizontal", label: "Horizontal" },
  { value: "vertical", label: "Vertical" },
];

type EstadoPlantillas =
  | { estado: "cargando" }
  | { estado: "error"; mensaje: string }
  | { estado: "listo"; meta: Record<Orientacion, PlantillaMeta | null> };

const nombreMes = (mes: string) => MESES[DateTime.fromISO(`${mes}-01`).month - 1];

function etiquetaMes(mes: string): string {
  const dt = DateTime.fromISO(`${mes}-01`);
  const nombre = MESES[dt.month - 1];
  return `${nombre.charAt(0).toUpperCase()}${nombre.slice(1)} ${dt.year}`;
}

/** «vie 30 oct»: el mes se agrega solo si el día cae fuera del mes elegido. */
function fechaCorta(dt: DateTime, mes: string): string {
  return `${DOW3[dt.weekday - 1]} ${dt.day}${dt.toFormat("yyyy-MM") !== mes ? " " + MON3[dt.month - 1] : ""}`;
}

/** «El 14 y el 22 no tienen farmacias cargadas.» */
function textoDiasSinFarmacia(dias: number[]): string {
  const partes = dias.map(d => `el ${d}`);
  const lista = partes.length === 1 ? partes[0] : `${partes.slice(0, -1).join(", ")} y ${partes[partes.length - 1]}`;
  return `${lista.charAt(0).toUpperCase()}${lista.slice(1)} ${dias.length === 1 ? "no tiene" : "no tienen"} farmacias cargadas.`;
}

function descargarArchivo(bytes: Uint8Array, nombre: string) {
  const url = URL.createObjectURL(new Blob([bytes], { type: "application/pdf" }));
  const a = document.createElement("a");
  a.href = url;
  a.download = nombre;
  a.click();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

const chip = "inline-flex h-11 items-center gap-2 rounded-full border pl-2.5 pr-3.5 text-[13px] text-adm-t70 md:h-9";
const tick = "grid h-5 w-5 shrink-0 place-items-center rounded-full";

function TildePlantilla({ nombre, ok, onCargar }: { nombre: Orientacion; ok: boolean; onCargar: () => void }) {
  if (ok) {
    return (
      <span className={cx(chip, "border-adm-border bg-adm-surface")}>
        <span className={`${tick} bg-adm-acento text-adm-sobre-acento`}><Check size={12} strokeWidth={3} /></span>
        Plantilla {nombre}
      </span>
    );
  }
  return (
    <span className={`${chip} border-adm-ambar-borde bg-adm-ambar-suave`}>
      <span className={`${tick} border-[1.5px] border-dashed border-adm-ambar text-adm-ambar`}><Minus size={12} strokeWidth={3} /></span>
      Falta la plantilla {nombre} ·{" "}
      <button type="button" onClick={onCargar} className="text-adm-acento-texto underline underline-offset-2">Cargarla</button>
    </span>
  );
}

/** Dibujo aproximado del cartel en HTML, con los mismos textos que salen en el PDF. */
function VistaPrevia({ orientacion, dias, shifts, pharmacies }: { orientacion: Orientacion; dias: DateTime[]; shifts: Shift[]; pharmacies: Pharmacy[] }) {
  const h = orientacion === "horizontal";
  return (
    <div
      className={`flex flex-col gap-[3%] overflow-hidden rounded-[3px] bg-white px-[4%] pb-[4%] pt-[4.5%] text-[#111] shadow-[0_10px_30px_-12px_rgba(0,0,0,0.35)] ${
        h ? "aspect-[841.89/595.28] w-full" : "aspect-[595.28/841.89] w-full max-w-[420px]"
      }`}
    >
      <div className="text-center text-[clamp(9px,1.3vw,16px)] font-bold tracking-[-0.01em] text-[#2f6f4f]">Farmacias de turno · Tandil</div>
      <div className={`grid flex-1 gap-[2.5%] ${h ? "grid-cols-4" : "grid-cols-2 grid-rows-2"}`}>
        {dias.map(d => {
          const fecha = d.toFormat("yyyy-MM-dd");
          const lista = farmaciasDelDia(fecha, shifts, pharmacies);
          return (
            <div key={fecha} className={`flex min-w-0 flex-col ${h ? "gap-[5%]" : "gap-1"}`}>
              <div className="rounded-[3px] bg-[#2f6f4f] px-[2%] py-[4%] text-center text-[clamp(6px,0.8vw,10px)] font-bold uppercase text-white">{etiquetaDia(d)}</div>
              {lista.length ? (
                lista.map(p => (
                  <div key={p.id} className={`flex min-w-0 flex-col gap-px rounded-[3px] bg-[#eef4f0] ${h ? "p-[5%]" : "px-[5%] py-1 leading-tight"}`}>
                    <b className="truncate text-[clamp(5px,0.75vw,9.5px)]">{nombreEnCartel(p)}</b>
                    <span className="truncate text-[clamp(4px,0.58vw,7.5px)] text-[#555]">{p.direccion.trim()}</span>
                    {p.telefono && <span className="truncate text-[clamp(4px,0.58vw,7.5px)] text-[#555]">{p.telefono.trim()}</span>}
                  </div>
                ))
              ) : (
                <div className="rounded-[3px] bg-[#fbf3e2] p-[5%]">
                  <b className="text-[clamp(5px,0.75vw,9.5px)] text-[#9a6a12]">Sin farmacia</b>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

interface Props {
  shifts: Shift[];
  pharmacies: Pharmacy[];
  onNavigate: (view: "turnos" | "cfg:plantillas") => void;
}

export default function CartelesPage({ shifts, pharmacies, onNavigate }: Props) {
  const toast = useToast();
  const [mes, setMes] = useState(() => DateTime.now().toFormat("yyyy-MM"));
  const [apagadas, setApagadas] = useState<Set<string>>(() => new Set());
  const [pv, setPv] = useState(0);
  const [orientacion, setOrientacion] = useState<Orientacion>("horizontal");
  const [plantillas, setPlantillas] = useState<EstadoPlantillas>({ estado: "cargando" });
  const [generando, setGenerando] = useState(false);
  // P-24: el vertical baja en cola; si el navegador lo frena, queda este botón
  const [verticalPendiente, setVerticalPendiente] = useState<{ bytes: Uint8Array; nombre: string } | null>(null);
  useEffect(() => setVerticalPendiente(null), [mes]);

  const revisarPlantillas = async () => {
    setPlantillas({ estado: "cargando" });
    try {
      const [horizontal, vertical] = await Promise.all([leerMetaPlantilla("horizontal"), leerMetaPlantilla("vertical")]);
      setPlantillas({ estado: "listo", meta: { horizontal, vertical } });
    } catch (e) {
      setPlantillas({ estado: "error", mensaje: motivoDeFalla(e) });
    }
  };

  useEffect(() => {
    revisarPlantillas();
  }, []);

  const meses = useMemo(() => {
    const set = new Set(shifts.map(s => s.date.slice(0, 7)));
    const hoy = DateTime.now();
    set.add(hoy.toFormat("yyyy-MM"));
    set.add(hoy.plus({ months: 1 }).toFormat("yyyy-MM"));
    return [...set].filter(m => /^\d{4}-\d{2}$/.test(m)).sort();
  }, [shifts]);

  const hojas = useMemo(() => armarHojas(mes), [mes]);
  const elegidas = hojas.filter(h => !apagadas.has(h));
  const pvIdx = Math.min(pv, Math.max(hojas.length - 1, 0));
  const diasPv = hojas.length ? diasDeHoja(hojas[pvIdx]) : [];

  const sinFarmacia = useMemo(() => {
    const inicio = DateTime.fromISO(`${mes}-01`);
    const dias = Array.from({ length: inicio.daysInMonth ?? 0 }, (_, i) => inicio.plus({ days: i }));
    return dias.filter(d => farmaciasDelDia(d.toFormat("yyyy-MM-dd"), shifts, pharmacies).length === 0).map(d => d.day);
  }, [mes, shifts, pharmacies]);
  const mesVacio = sinFarmacia.length === (DateTime.fromISO(`${mes}-01`).daysInMonth ?? 0);

  const cambiarMes = (nuevo: string) => {
    setMes(nuevo);
    setApagadas(new Set());
    setPv(0);
  };

  const alternar = (hoja: string) => {
    setApagadas(prev => {
      const next = new Set(prev);
      if (next.has(hoja)) next.delete(hoja);
      else next.add(hoja);
      return next;
    });
  };

  const todasONinguna = () => setApagadas(elegidas.length ? new Set(hojas) : new Set());

  const descargar = async () => {
    if (plantillas.estado !== "listo" || !plantillas.meta.horizontal || !plantillas.meta.vertical) {
      toast.show("Falta una plantilla: no se pudieron armar los carteles", "error");
      return;
    }
    setGenerando(true);
    try {
      const [horizontal, vertical] = await Promise.all([bajarPlantilla("horizontal"), bajarPlantilla("vertical")]);
      const pdfs = await generarCarteles({ horizontal, vertical }, { hojas: elegidas, preset: await cargarPreset(), shifts, pharmacies });
      descargarArchivo(pdfs.horizontal, `Farmacias_Turno_${mes}_horizontal.pdf`);
      const verticalPdf = { bytes: pdfs.vertical, nombre: `Farmacias_Turno_${mes}_vertical.pdf` };
      setVerticalPendiente(verticalPdf);
      await new Promise(resolve => setTimeout(resolve, 1500));
      descargarArchivo(verticalPdf.bytes, verticalPdf.nombre);
      toast.show(`Descargados los dos PDF · ${elegidas.length} hojas cada uno`);
    } catch (e) {
      toast.show(`No se pudieron armar los carteles. ${motivoDeFalla(e)}`, "error");
    } finally {
      setGenerando(false);
    }
  };

  return (
    <div className="mx-auto flex max-w-[1160px] flex-col gap-5 md:gap-7">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div className="flex flex-col gap-1">
          <span className="text-xs font-medium text-adm-acento-texto">Para imprimir</span>
          <h1 className="text-[22px] font-semibold leading-tight tracking-[-0.02em] text-adm-text md:text-[26px]">Carteles</h1>
          <p className="text-[13px] leading-normal text-adm-t55">Una hoja cada cuatro días, en horizontal y en vertical.</p>
        </div>
        <div className="flex w-full md:w-auto">
          <Button variant="primary" className="flex-1 md:flex-none" icon={<Download {...ICON} />} loading={generando} onClick={descargar} disabled={elegidas.length === 0}>
            Descargar carteles
          </Button>
        </div>
      </div>

      {verticalPendiente && !generando && (
        <Alert
          tone="amber"
          icon={Download}
          action={
            <Button size="sm" onClick={() => descargarArchivo(verticalPendiente.bytes, verticalPendiente.nombre)}>
              Bajar el vertical
            </Button>
          }
        >
          Si el vertical no bajó, tu navegador lo frenó: bajalo con este botón.
        </Alert>
      )}

      <div className="flex flex-wrap gap-2" aria-live="polite">
        {plantillas.estado === "cargando" && (
          <span className={`${chip} border-adm-border bg-adm-surface`}>
            <Spinner />
            Revisando plantillas…
          </span>
        )}
        {plantillas.estado === "listo" &&
          ORIENTACIONES.map(o => (
            <Fragment key={o.value}>
              <TildePlantilla nombre={o.value} ok={!!plantillas.meta[o.value]} onCargar={() => onNavigate("cfg:plantillas")} />
            </Fragment>
          ))}
      </div>

      {plantillas.estado === "error" && (
        <Alert tone="red" action={<Button size="sm" onClick={revisarPlantillas}>Reintentar</Button>}>
          No se pudieron revisar las plantillas. {plantillas.mensaje}
        </Alert>
      )}

      {!mesVacio && sinFarmacia.length > 0 && (
        <Alert tone="amber" action={<Button size="sm" onClick={() => onNavigate("turnos")}>Completar</Button>}>
          {textoDiasSinFarmacia(sinFarmacia)}{" "}
          <span className="text-adm-t55">{sinFarmacia.length === 1 ? "Su columna sale vacía." : "Sus columnas salen vacías."}</span>
        </Alert>
      )}

      <div className="grid grid-cols-1 items-start gap-5 min-[1001px]:grid-cols-[minmax(0,360px)_minmax(0,1fr)]">
        <Card className="flex flex-col gap-3.5 p-[18px]">
          <Field label="Mes" htmlFor="carteles-mes">
            <Select id="carteles-mes" value={mes} onChange={e => cambiarMes(e.target.value)}>
              {meses.map(m => (
                <option key={m} value={m}>{etiquetaMes(m)}</option>
              ))}
            </Select>
          </Field>

          {mesVacio ? (
            <Empty size="sm" title={`Sin turnos en ${nombreMes(mes)}`}>Las hojas se arman con los turnos del mes.</Empty>
          ) : (
            <>
              <div className="flex items-center justify-between">
                <span className="text-xs font-medium text-adm-t55">{elegidas.length} de {hojas.length} hojas</span>
                <Button variant="ghost" size="sm" onClick={todasONinguna}>{elegidas.length ? "Ninguna" : "Todas"}</Button>
              </div>
              <div className="-mx-1.5 flex max-h-[420px] flex-col gap-0.5 overflow-auto">
                {hojas.map((hoja, i) => {
                  const on = !apagadas.has(hoja);
                  const dias = diasDeHoja(hoja);
                  return (
                    <div key={hoja} className={`flex h-11 shrink-0 items-center rounded-[10px] pr-2.5 ${i === pvIdx ? "bg-adm-card" : "hover:bg-adm-t5"}`}>
                      <button
                        type="button"
                        role="checkbox"
                        aria-checked={on}
                        aria-label={`Incluir la hoja ${i + 1}`}
                        onClick={() => alternar(hoja)}
                        className="grid h-11 w-11 shrink-0 place-items-center rounded-[10px]"
                      >
                        <span className={`grid h-[18px] w-[18px] place-items-center rounded-[5px] border-[1.5px] ${on ? "border-adm-acento bg-adm-acento text-adm-sobre-acento" : "border-adm-border-2 bg-adm-surface"}`}>
                          {on && <Check size={12} strokeWidth={3} />}
                        </span>
                      </button>
                      <button type="button" onClick={() => setPv(i)} aria-current={i === pvIdx} className="flex h-11 min-w-0 flex-1 items-center gap-3 text-left">
                        <span className="flex-1 text-sm text-adm-text">Hoja {i + 1}</span>
                        <span className="text-[13px] text-adm-t55">{fechaCorta(dias[0], mes)} al {fechaCorta(dias[3], mes)}</span>
                      </button>
                    </div>
                  );
                })}
              </div>
            </>
          )}
        </Card>

        <Card className="flex flex-col gap-3.5 p-[18px] min-[1001px]:sticky min-[1001px]:top-[120px]">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <Seg label="Orientación de la vista previa" options={ORIENTACIONES} value={orientacion} onChange={setOrientacion} />
            {!mesVacio && diasPv.length > 0 && <span className="text-[13px] text-adm-t55">{fechaCorta(diasPv[0], mes)} al {fechaCorta(diasPv[3], mes)}</span>}
          </div>
          <div className="grid place-items-center rounded-xl bg-adm-card p-3 md:min-h-[300px] md:p-5">
            {mesVacio || diasPv.length === 0 ? (
              <span className="text-[13px] text-adm-t55">Sin hojas para mostrar</span>
            ) : (
              <VistaPrevia orientacion={orientacion} dias={diasPv} shifts={shifts} pharmacies={pharmacies} />
            )}
          </div>
          <div className="flex items-center justify-between gap-2">
            <Button size="sm" icon={<ChevronLeft {...ICON} />} disabled={mesVacio} onClick={() => setPv((pvIdx + hojas.length - 1) % hojas.length)}>
              Anterior
            </Button>
            <span className="text-xs font-medium text-adm-t55">Hoja {pvIdx + 1} de {hojas.length}</span>
            <Button size="sm" iconRight={<ChevronRight {...ICON} />} disabled={mesVacio} onClick={() => setPv((pvIdx + 1) % hojas.length)}>
              Siguiente
            </Button>
          </div>
        </Card>
      </div>
    </div>
  );
}
